import * as S from './styled'

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from 'react-responsive'
import { t } from 'i18next'
import i18n from '@/constant/locales/i18n'
import api from '@/core/services/api'
import { formatMoney } from '@/constant/currency'
import Header from '@/components/common/Header'
import Footer from '@/components/Footer'
import QuickNav from '@/components/QuickNav'
import MHeader from '@/components/MHeader'
import MFooter from '@/components/MFooter'
import MHistory from '@/components/MHistory'

const PER_PAGE = 10

// 게임종류 탭 (전체 / 라이브=C / 슬롯=S)
const GAME_TYPES = [
  { v: '', k: 'all' }, { v: 'C', k: 'live' }, { v: 'S', k: 'slot' },
]
// 결과 탭 (transaction_type)
const RESULTS = [
  { v: '', k: 'all' }, { v: 'B', k: 'betting' }, { v: 'W', k: 'win' },
  { v: 'L', k: 'lose' }, { v: 'C', k: 'cancel' }, { v: 'T', k: 'tie' },
]
const PERIODS = [
  { v: 'TODAY', k: 'today' }, { v: 'YESTERDAY', k: 'yesterday' },
  { v: 'WEEK', k: 'week' }, { v: 'MONTH', k: 'month' },
]

const fmtDt = (s) => {
  if (!s) return '-'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const p = (x) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}
// 베팅/당첨 금액도 user_decimal_places 자릿수로 절삭 표시(0.5 등 소수 정상)
const fmtNum = (n) => formatMoney(n)

// 행 결과 파생:
//   W & 당첨금=0 → 낙첨 / 라이브카지노(C) & 당첨금==베팅금(원금반환) → 타이 / W & 당첨금>0 → 당첨
//   C → 취소 / B(정산 전) → 베팅중  (transaction_type='T' 도 타이로 대비)
const rowResult = (item) => {
  const bet = Math.abs(Number(item.bet_amount) || 0)
  const win = Number(item.win_amount) || 0
  if (item.transaction_type === 'W') {
    if (win === 0) return { code: 'L', k: 'lose' }
    if (item.game_type === 'C' && win === bet) return { code: 'T', k: 'tie' }
    return { code: 'W', k: 'win' }
  }
  if (item.transaction_type === 'T') return { code: 'T', k: 'tie' }
  if (item.transaction_type === 'C') return { code: 'C', k: 'cancel' }
  return { code: 'B', k: 'progress' }
}

/** 커스텀 드롭다운 (네이티브 select 대체) */
function Dropdown({ value, options, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  const sel = options.find((o) => o.value === value) || options[0]
  return (
    <S.Dropdown ref={ref}>
      <S.DropdownBtn type="button" $open={open} onClick={() => setOpen((v) => !v)}>
        <span>{sel?.label}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </S.DropdownBtn>
      {open && (
        <S.DropdownMenu>
          {options.map((o) => (
            <S.DropdownItem key={o.value} $active={o.value === value} onClick={() => { onChange(o.value); setOpen(false) }}>
              {o.label}
            </S.DropdownItem>
          ))}
        </S.DropdownMenu>
      )}
    </S.Dropdown>
  )
}

/**
 * 베팅 내역 페이지 (플로팅 히스토리 버튼 → 이 페이지로 이동)
 */
export default function BetHistory() {
  const [gameType, setGameType] = useState('')
  const [result, setResult] = useState('')       // transaction_type
  const [timeType, setTimeType] = useState('TODAY')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const isMobile = useMediaQuery({ maxWidth: 850 })
  const navigate = useNavigate()

  const fetchData = useCallback((opts = {}) => {
    const params = {
      game_type: opts.gameType ?? gameType,
      transaction_type: opts.result ?? result,
      time_type: opts.timeType ?? timeType,
      game_search: opts.search ?? search,
      page: opts.page ?? 1,
      countPerPage: PER_PAGE,
      lang: i18n.language,
    }
    setLoading(true)
    api.get('/account/bet/log', { params })
      .then((res) => {
        const content = res.data?.content
        const list = Array.isArray(content) ? content : (content?.content || [])
        setRows(list)
        const ph = content?.pageHelperDto || res.data?.pageHelperDto
        setTotal(ph?.totalRecords ?? list.length)
        setPage(params.page)
      })
      .catch((err) => { console.error(err); setRows([]); setTotal(0) })
      .finally(() => setLoading(false))
  }, [gameType, result, timeType, search])

  useEffect(() => { fetchData({ page: 1 }) }, []) // 최초 오늘 기준

  // 탭은 즉시 조회, 드롭다운/검색어는 검색 버튼에서
  const onGameType = (v) => { setGameType(v); fetchData({ gameType: v, page: 1 }) }
  const onResult = (v) => { setResult(v); fetchData({ result: v, page: 1 }) }
  const onSearch = () => fetchData({ page: 1 })
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))

  return (
    <>
    {isMobile ? <MHeader /> : <Header />}
    <S.Page>
      <S.BackBtn type="button" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        {t('betHistory.back')}
      </S.BackBtn>

      {/* 계정 정보 (필터) */}
      <S.Card>
        <S.CardTitle>
          <S.TitleIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="4" y1="7" x2="20" y2="7" /><circle cx="10" cy="7" r="2.6" fill="#12161d" />
              <line x1="4" y1="12" x2="20" y2="12" /><circle cx="16" cy="12" r="2.6" fill="#12161d" />
              <line x1="4" y1="17" x2="20" y2="17" /><circle cx="8" cy="17" r="2.6" fill="#12161d" />
            </svg>
          </S.TitleIcon>
          {t('betHistory.accountInfo')}
        </S.CardTitle>
        <S.FilterRow>
          <Dropdown
            value={timeType}
            onChange={setTimeType}
            options={PERIODS.map((p) => ({ value: p.v, label: t('betHistory.period.' + p.k) }))}
          />
          <S.SearchBtn onClick={onSearch}>{t('betHistory.search')}</S.SearchBtn>
        </S.FilterRow>

        <S.GroupLabel>{t('betHistory.gameType')}</S.GroupLabel>
        <S.Tabs>
          {GAME_TYPES.map((g) => (
            <S.Tab key={g.k} $active={gameType === g.v} $variant="game" onClick={() => onGameType(g.v)}>
              {t('betHistory.gt.' + g.k)}
            </S.Tab>
          ))}
        </S.Tabs>

        <S.GroupLabel>{t('betHistory.result')}</S.GroupLabel>
        <S.Tabs>
          {RESULTS.map((r) => (
            <S.Tab key={r.k} $active={result === r.v} $variant="result" onClick={() => onResult(r.v)}>
              {t('betHistory.res.' + r.k)}
            </S.Tab>
          ))}
        </S.Tabs>
      </S.Card>

      {/* 베팅 기록 (테이블) */}
      <S.Card>
        <S.CardTitle>
          <S.TitleIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v5h5" />
              <path d="M3.5 13a9 9 0 1 0 2.6-7.1L3 8" />
              <path d="M12 7.5V12l3 1.8" />
            </svg>
          </S.TitleIcon>
          {t('betHistory.title')}
        </S.CardTitle>
        {isMobile ? (
          /* 모바일: 카드 리스트 */
          rows.length === 0 ? (
            <S.MEmpty>{loading ? '…' : t('error.no_data')}</S.MEmpty>
          ) : (
            <S.MList>
              {rows.map((item, i) => {
                const gt = GAME_TYPES.find((g) => g.v === item.game_type)
                const rr = rowResult(item)
                return (
                  <S.MCard key={item.round || i}>
                    <S.MHead>
                      <span className="name">{item.game_title || item.vendor_name || '-'}</span>
                      <S.ResultBadge $code={rr.code}>{t('betHistory.res.' + rr.k)}</S.ResultBadge>
                    </S.MHead>
                    <S.MRow><span className="k">{t('betHistory.col.date')}</span><span className="v">{fmtDt(item.bet_at)}</span></S.MRow>
                    <S.MRow><span className="k">{t('betHistory.col.gameType')}</span><span className="v">{gt ? t('betHistory.gt.' + gt.k) : (item.game_type || '-')}</span></S.MRow>
                    <S.MRow><span className="k">{t('betHistory.col.currency')}</span><span className="v">{item.currency_type || '-'}</span></S.MRow>
                    <S.MRow><span className="k">{t('betHistory.col.betAmount')}</span><span className="v">{fmtNum(item.bet_amount)}</span></S.MRow>
                    <S.MRow><span className="k">{t('betHistory.col.winAmount')}</span><span className="v win">{fmtNum(item.win_amount)}</span></S.MRow>
                  </S.MCard>
                )
              })}
            </S.MList>
          )
        ) : (
          <S.TableWrap>
            <S.Table>
              <thead>
                <tr>
                  <S.Th>{t('betHistory.col.date')}</S.Th>
                  <S.Th>{t('betHistory.col.gameType')}</S.Th>
                  <S.Th $align="left">{t('betHistory.col.gameName')}</S.Th>
                  <S.Th>{t('betHistory.col.currency')}</S.Th>
                  <S.Th $align="right">{t('betHistory.col.betAmount')}</S.Th>
                  <S.Th $align="right">{t('betHistory.col.winAmount')}</S.Th>
                  <S.Th>{t('betHistory.col.result')}</S.Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7}><S.Empty>{loading ? '…' : t('error.no_data')}</S.Empty></td>
                  </tr>
                ) : (
                  rows.map((item, i) => {
                    const gt = GAME_TYPES.find((g) => g.v === item.game_type)
                    const rr = rowResult(item)
                    return (
                      <tr key={item.round || i}>
                        <S.Td>{fmtDt(item.bet_at)}</S.Td>
                        <S.Td>{gt ? t('betHistory.gt.' + gt.k) : (item.game_type || '-')}</S.Td>
                        <S.Td $align="left">{item.game_title || item.vendor_name || '-'}</S.Td>
                        <S.Td>{item.currency_type || '-'}</S.Td>
                        <S.Td $align="right">{fmtNum(item.bet_amount)}</S.Td>
                        <S.Td $align="right">{fmtNum(item.win_amount)}</S.Td>
                        <S.Td><S.ResultBadge $code={rr.code}>{t('betHistory.res.' + rr.k)}</S.ResultBadge></S.Td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </S.Table>
          </S.TableWrap>
        )}

        {totalPages > 1 && (
          <S.Pager>
            <S.PageBtn disabled={page <= 1} onClick={() => fetchData({ page: page - 1 })}>&lt;</S.PageBtn>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, idx) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4))
              const num = start + idx
              if (num > totalPages) return null
              return (
                <S.PageBtn key={num} $active={page === num} onClick={() => fetchData({ page: num })}>{num}</S.PageBtn>
              )
            })}
            <S.PageBtn disabled={page >= totalPages} onClick={() => fetchData({ page: page + 1 })}>&gt;</S.PageBtn>
          </S.Pager>
        )}
      </S.Card>
    </S.Page>
    {isMobile ? (<><MFooter /><MHistory /></>) : (<><Footer /><QuickNav /></>)}
    </>
  )
}
